import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ShieldCheck, ArrowRight, ChevronDown, ChevronUp, Eye, Clock, MessageCircle, Users, BarChart2 } from 'lucide-react';
import useInView from '../hooks/useInView';

const SHIELD = "/images/home/safety-research-hero.png";

const FadeIn = ({ children, delay = 0, className = '' }) => {
  const [ref, inView] = useInView(0.1);
  return (
    <div ref={ref} className={`transition-all duration-700 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'} ${className}`} style={{ transitionDelay: `${delay}ms` }}>
      {children}
    </div>
  );
};

const SectionLabel = ({ children }) => (
  <p className="text-xs font-semibold tracking-[0.2em] uppercase text-purple-400 mb-3 font-outfit">{children}</p>
);

const faqs = [
  {
    q: "Is ROMIND KIDS safe for my child?",
    a: "ROMIND KIDS is designed with child safety principles at every layer. Features include RONI AI-assisted safety guidance, friend-based messaging, reporting and blocking flows, and age-aware content structure. While no platform can guarantee absolute safety, every product decision is made with child protection in mind."
  },
  {
    q: "What age group is ROMIND KIDS designed for?",
    a: "ROMIND KIDS is specifically designed for children aged 9–14. The experience, language, content structure, and safety logic are all calibrated to this developmental stage."
  },
  {
    q: "Can my child message strangers?",
    a: "Messaging in ROMIND KIDS is designed around the friends system. The platform is built to limit unsolicited contact, with RONI safety guidance, reporting tools, and blocking functionality integrated directly into the communication experience."
  },
  {
    q: "What is RONI?",
    a: "RONI is the AI-assisted safety companion built into ROMIND KIDS. RONI's role is not surveillance — it is in-app guidance. RONI looks for unsafe wording, risky interaction patterns, and potential harm in real time, and offers gentle prompts to the child in the moment (suggesting kinder phrasing, flagging suspicious contact, or surfacing reporting and blocking tools when needed). RONI supports child decision-making rather than replacing it, and works alongside reporting flows, blocking, friend-based messaging, and platform-level moderation. No AI guarantees absolute safety, but RONI is one continuous layer in a stack of safety design choices."
  },
  {
    q: "How is ROMIND KIDS different from other social apps?",
    a: "Most social platforms are designed around engagement maximization — feeds optimized to keep users scrolling, follower metrics that drive comparison, and viral mechanics borrowed from adult networks. ROMIND KIDS is designed differently at the platform level. The app design and experience inside the platform — clubs, challenges, the Create Studio, identity, communication — is built for children aged 9–14. The safety, privacy, and trust architecture is built for parents. Same product, two coexisting layers, each calibrated for its audience. There is no infinite feed, no follower competition, no dislike mechanics, no random real-money rewards, and no adult dating positioning. The default is creativity, not consumption."
  },
  {
    q: "Will there be advertising or real-money mechanics?",
    a: "ROMIND KIDS is designed without gambling mechanics and without random real-money reward systems. The platform is being built with a family-conscious monetization approach. Final policies will be clearly communicated before launch."
  },
  {
    q: "What is the Times System?",
    a: "Times is a unique gifting feature where children can share limited-time cards with friends under structured, controlled rules. It's designed to encourage positive peer interaction without creating addictive loop mechanics."
  },
  {
    q: "When will the app be available?",
    a: "ROMIND KIDS is currently in active development. Mobile launch via Google Play is being prepared. Subscribe or check back for launch updates."
  },
];

const FaqItem = ({ item, index }) => {
  const [open, setOpen] = useState(false);
  return (
    <div data-testid={`faq-item-${index}`} className="glass-card overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        data-testid={`faq-toggle-${index}`}
        className="w-full flex items-center justify-between p-5 text-left transition-colors hover:bg-white/3"
      >
        <span className="font-outfit font-semibold text-white text-sm pr-4">{item.q}</span>
        {open ? <ChevronUp size={16} className="text-purple-400 flex-shrink-0" /> : <ChevronDown size={16} className="text-white/40 flex-shrink-0" />}
      </button>
      {open && (
        <div className="px-5 pb-5">
          <div className="border-t border-white/5 pt-4">
            <p className="text-white/55 text-sm leading-relaxed">{item.a}</p>
          </div>
        </div>
      )}
    </div>
  );
};

const Parents = () => (
  <main className="bg-[#05050A] min-h-screen">
    {/* Hero */}
    <section className="relative pt-32 pb-20 overflow-hidden">
      <div className="absolute top-1/3 left-1/4 w-80 h-80 bg-purple-600/15 rounded-full blur-[120px] pointer-events-none" />
      <div className="absolute bottom-1/4 right-1/4 w-64 h-64 bg-cyan-500/10 rounded-full blur-[100px] pointer-events-none" />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionLabel>For Parents</SectionLabel>
        <h1 className="font-outfit font-black text-5xl sm:text-6xl text-white mb-6 tracking-tight leading-[1.05] max-w-3xl">
          A creative platform for your child.<br />
          <span className="gradient-text">A safety architecture for you.</span>
        </h1>
        <p className="text-white/60 text-base sm:text-lg leading-relaxed max-w-2xl">
          The app design inside ROMIND KIDS is built for children aged 9–14 — their creativity, clubs, challenges, and identity. The safety, privacy, and trust layer is built for parents. Same platform, two coexisting layers, each calibrated for its audience.
        </p>
      </div>
    </section>

    {/* Problem context */}
    <section className="py-16 bg-[#080714]">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <FadeIn>
          <SectionLabel>The Context</SectionLabel>
          <h2 className="font-outfit font-bold text-3xl text-white mb-8 tracking-tight max-w-2xl">
            Why current platforms aren't designed for children.
          </h2>
        </FadeIn>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {[
            { icon: BarChart2, title: "Engagement over wellbeing", desc: "Most platforms are optimized for maximum time on screen — not for healthy digital habits." },
            { icon: Eye, title: "Low parental visibility", desc: "Parents often have minimal insight into what their child is actually experiencing on these platforms." },
            { icon: MessageCircle, title: "Unfiltered communication", desc: "Default settings often allow contact from strangers, with weak age-based filtering." },
            { icon: Clock, title: "No purposeful structure", desc: "Endless feeds without category structure make it difficult for children to use time meaningfully." },
            { icon: ShieldCheck, title: "Safety as an afterthought", desc: "Child safety features are often added reactively, not designed into the platform from the start." },
            { icon: Users, title: "Adult social dynamics", desc: "Follower counts, viral mechanics, and comparison culture are borrowed from adult networks." },
          ].map((item, i) => (
            <FadeIn key={i} delay={i * 60}>
              <div data-testid={`parents-problem-${i}`} className="glass-card p-5">
                <div className="w-9 h-9 rounded-xl bg-red-500/10 border border-red-500/15 flex items-center justify-center mb-3">
                  <item.icon size={15} className="text-red-400" />
                </div>
                <h3 className="font-outfit font-semibold text-white text-sm mb-1.5">{item.title}</h3>
                <p className="text-white/45 text-xs leading-relaxed">{item.desc}</p>
              </div>
            </FadeIn>
          ))}
        </div>
      </div>
    </section>

    {/* ROMIND KIDS approach */}
    <section className="py-16">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <FadeIn>
            <SectionLabel>What ROMIND KIDS Offers</SectionLabel>
            <h2 className="font-outfit font-bold text-3xl text-white mb-6 tracking-tight">
              A different kind of social experience.
            </h2>
            <div className="space-y-4">
              {[
                { title: "Designed for ages 9–14", desc: "Every interaction, every feature, every design decision is made with this specific age group in mind." },
                { title: "RONI AI-assisted safety", desc: "An AI companion that helps detect unsafe patterns, risky interactions, and harmful wording in real time." },
                { title: "Friend-based communication", desc: "Messaging structured around known friends, with safety tools built into every conversation." },
                { title: "Creative over consumptive", desc: "Tools for expression, clubs, and challenges — designed to replace passive scrolling with purposeful participation." },
                { title: "Privacy-conscious profile design", desc: "Children can have a public identity without exposing private or sensitive personal data." },
                { title: "No negative reaction culture", desc: "No dislike buttons. No public pressure metrics designed to create social anxiety." },
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-3">
                  <div className="w-5 h-5 rounded-full bg-emerald-500/20 border border-emerald-500/35 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <div className="w-2 h-2 rounded-full bg-emerald-400" />
                  </div>
                  <div>
                    <p className="font-semibold text-white text-sm mb-0.5">{item.title}</p>
                    <p className="text-white/45 text-xs leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </FadeIn>
          <FadeIn delay={200} className="flex justify-center">
            <div className="relative">
              <div className="absolute inset-0 bg-emerald-500/15 rounded-full blur-[80px]" />
              <img src={SHIELD} alt="Child safety shield" className="relative w-full max-w-xs" />
            </div>
          </FadeIn>
        </div>
      </div>
    </section>

    {/* FAQ */}
    <section data-testid="faq-section" className="py-16 bg-[#080714]">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <FadeIn>
          <SectionLabel>Frequently Asked Questions</SectionLabel>
          <h2 className="font-outfit font-bold text-3xl text-white mb-8 tracking-tight">Your questions, answered.</h2>
        </FadeIn>
        <div className="space-y-3">
          {faqs.map((item, i) => (
            <FadeIn key={i} delay={i * 40}>
              <FaqItem item={item} index={i} />
            </FadeIn>
          ))}
        </div>
      </div>
    </section>

    {/* CTA */}
    <section className="py-16">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <FadeIn>
          <h2 className="font-outfit font-bold text-2xl text-white mb-4">Still have questions?</h2>
          <p className="text-white/50 text-sm mb-6">Reach out directly. We're a founder-led team and we read every message.</p>
          <Link to="/contact" data-testid="parents-contact-cta" className="inline-flex items-center gap-2 px-6 py-3 rounded-full font-semibold text-sm bg-purple-600/80 border border-purple-400/40 text-white hover:bg-purple-500 transition-all">
            Contact Us <ArrowRight size={14} />
          </Link>
        </FadeIn>
      </div>
    </section>
  </main>
);

export default Parents;
